import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableProps,
  TableRow,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useState } from 'react'
import { MoovyMutationError, MoovyPagination } from '..'

import { UseQueryResult } from '@tanstack/react-query'
import { FormattedMessage } from 'react-intl'
import { MoovyProgress } from '..'
import { MoovyPalette } from '../../styles/theme'
import { MoovyPaginationProps } from '../moovyPagination'

interface MoovyTableProps extends TableProps {
  items: any[]
  emptyTextId: string
  errorTextId: string
  query: UseQueryResult<any, any> | undefined
  pagination?: MoovyPaginationProps
}

interface MoovyEmptyTableProps {
  emptyTextId: string
  errorTextId: string
  query: UseQueryResult<any, any> | undefined
  children?: React.ReactNode
}

const StyledPaper = styled(Paper)({
  boxShadow: 'none',
})

const EmptyTable = (props: MoovyEmptyTableProps) => {
  const { children, query, emptyTextId, errorTextId } = props
  return (
    <>
      {React.Children.map(children, (child, i) => {
        // Parse only the first content which is the header of the table
        if (i > 1) return
        return child
      })}
      <TableBody>
        <TableRow>
          <TableCell colSpan={99}>
            {query?.isError ? (
              <div style={{ textAlign: 'center' }}>
                <MoovyMutationError
                  query={query}
                  errorTitleKey={
                    errorTextId || 'shared.moovyTable.action.failed'
                  }
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                {(emptyTextId && <FormattedMessage id={emptyTextId} />) || (
                  <FormattedMessage id="shared.moovyTable.action.emptySet" />
                )}
              </div>
            )}
          </TableCell>
        </TableRow>
      </TableBody>
    </>
  )
}

const ContentTable: React.FC<MoovyTableProps> = ({
  items,
  emptyTextId,
  errorTextId,
  query,
  pagination,
  ...tableProps
}) => {
  const [page, setPage] = useState(pagination?.page || 1)

  if (items?.length > 0 && tableProps.children) {
    return (
      <TableContainer component={StyledPaper}>
        <Table {...tableProps}>{tableProps.children}</Table>
        {pagination && pagination.total > pagination.limit && (
          <MoovyPagination
            page={page}
            total={pagination.total}
            limit={pagination.limit}
            onNextPage={(page) => {
              setPage(page)
              pagination.onNextPage(page)
            }}
          />
        )}
      </TableContainer>
    )
  } else {
    return (
      <Table {...tableProps}>
        <EmptyTable
          query={query}
          emptyTextId={emptyTextId}
          errorTextId={errorTextId}
        >
          {tableProps.children}
        </EmptyTable>
      </Table>
    )
  }
}

const LoadingTable = (props: MoovyTableProps) => {
  const isProgressVisible = false

  return (
    <React.Fragment>
      <Box
        sx={{
          opacity: 0.5,
          color: MoovyPalette.progressBlockContentColor,
          pointerEvents: 'none',
          position: 'relative',
        }}
      >
        <ContentTable {...props} />
        {!isProgressVisible && (
          <MoovyProgress
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          />
        )}
      </Box>
    </React.Fragment>
  )
}

const MoovyTable = (props: MoovyTableProps) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // As a default, the table is hidden in mobile view.
  const isHidden = props.hidden || isMobile

  return (
    !isHidden &&
    (props.query?.isLoading || props.query?.isFetching ? (
      <LoadingTable {...props} />
    ) : (
      <ContentTable {...props} />
    ))
  )
}

export default MoovyTable
